import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Lock, Check, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { GAME_MODES, GameModeId } from '../../../shared/constants';

interface GameModeSelectorProps {
  selectedMode: GameModeId;
  onSelectMode: (mode: GameModeId) => void;
  isPremium: boolean;
}

export default function GameModeSelector({ 
  selectedMode, 
  onSelectMode, 
  isPremium 
}: GameModeSelectorProps) {
  const [expandedMode, setExpandedMode] = useState<GameModeId | null>(null);
  
  const modes = Object.values(GAME_MODES);
  
  const handleModeClick = (modeId: GameModeId) => {
    const mode = GAME_MODES[modeId];
    
    // Si le mode est premium et le joueur n'est pas premium, on ne fait rien
    if (mode.isPremium && !isPremium) {
      return;
    }
    
    onSelectMode(modeId);
  };
  
  const toggleExpanded = (e: React.MouseEvent, modeId: GameModeId) => {
    e.stopPropagation();
    setExpandedMode(expandedMode === modeId ? null : modeId);
  };
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          🎮 Mode de Jeu
        </h3>
        {!isPremium && (
          <span className="text-xs text-yellow-400 flex items-center gap-1">
            <Crown className="w-3 h-3" />
            5 modes Premium
          </span>
        )}
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {modes.map((mode) => {
          const modeId = mode.id as GameModeId;
          const isSelected = selectedMode === modeId;
          const isLocked = mode.isPremium && !isPremium;
          const isExpanded = expandedMode === modeId;
          
          return (
            <motion.div
              key={modeId}
              layout
              className={`
                relative rounded-xl border-2 transition-all cursor-pointer overflow-hidden
                ${isSelected 
                  ? 'border-primary-500 bg-primary-500/20 shadow-lg shadow-primary-500/20' 
                  : isLocked
                    ? 'border-dark-600 bg-dark-700/50 opacity-60 cursor-not-allowed'
                    : 'border-dark-600 bg-dark-700 hover:border-dark-500 hover:bg-dark-600/50'
                }
              `}
              onClick={() => handleModeClick(modeId)}
              whileHover={!isLocked ? { scale: 1.02 } : {}}
              whileTap={!isLocked ? { scale: 0.98 } : {}}
            >
              {/* Header */}
              <div className={`p-4 ${isExpanded ? 'pb-2' : ''}`}>
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`
                    w-12 h-12 rounded-xl flex items-center justify-center text-2xl
                    bg-gradient-to-br ${mode.color}
                  `}>
                    {mode.icon}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold text-white truncate">{mode.name}</h4>
                      {mode.isPremium && (
                        <Crown className={`w-4 h-4 flex-shrink-0 ${isLocked ? 'text-dark-400' : 'text-yellow-400'}`} />
                      )}
                    </div>
                    <p className="text-sm text-dark-400 line-clamp-2">
                      {mode.description}
                    </p>
                  </div>
                  
                  {/* Status indicators */}
                  <div className="flex flex-col items-center gap-1">
                    {isSelected && (
                      <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    {isLocked && (
                      <div className="w-6 h-6 bg-dark-600 rounded-full flex items-center justify-center">
                        <Lock className="w-4 h-4 text-dark-400" />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Expand button */}
                <button
                  onClick={(e) => toggleExpanded(e, modeId)}
                  className="mt-2 text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1 transition-colors"
                >
                  <Info className="w-3 h-3" />
                  {isExpanded ? 'Moins de détails' : 'Plus de détails'}
                  {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>
              
              {/* Expanded details */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 pt-2 border-t border-dark-600/50">
                      <p className="text-sm text-dark-300 mb-3">
                        {mode.longDescription}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {mode.features.map((feature, i) => (
                          <span 
                            key={i}
                            className={`text-xs px-2 py-1 rounded-full bg-gradient-to-r ${mode.color} bg-opacity-20`}
                            style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
                          >
                            ✓ {feature}
                          </span>
                        ))}
                      </div>
                      
                      {isLocked && (
                        <div className="mt-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                          <p className="text-xs text-yellow-400 flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            Passez Premium pour débloquer ce mode
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
      
      {/* Message pour joueurs non-premium */}
      {!isPremium && (
        <div className="text-center text-sm text-dark-400 mt-4">
          <p>
            Le mode <strong className="text-white">Classique</strong> est disponible pour tous. 
            <span className="text-yellow-400 ml-1">Passez Premium</span> pour accéder aux 5 autres modes !
          </p>
        </div>
      )}
    </div>
  );
}
